import { ChartData, ChartOptions, Plugin } from 'chart.js'
import { BabyGrowthPercentiles } from '~/interfaces'

interface IAnnotation {
  readonly x: number
  readonly y: number
}

interface IAxisLabels {
  readonly x: string
  readonly y: string
}

const drawBorderRadius = {
  id: 'drawBorderRadius',
  afterDraw(chart) {
    const { ctx, chartArea } = chart
    const { left, top, bottom, right, width, height } = chartArea

    // delete old lines
    ctx.save()
    ctx.beginPath()
    ctx.lineWidth = 1
    ctx.strokeStyle = '#ffff'
    ctx.moveTo(left, top)
    ctx.lineTo(right, top)
    ctx.moveTo(left, top)
    ctx.lineTo(left, bottom)
    ctx.moveTo(left, bottom)
    ctx.lineTo(right, bottom)
    ctx.moveTo(right, top)
    ctx.lineTo(right, bottom)
    ctx.stroke()
    ctx.restore()

    // redraw new lines with border radius
    ctx.beginPath()
    ctx.save()
    ctx.strokeStyle = '#ECEFF4'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.setLineDash([])
    ctx.roundRect(left, top, width, height, 6)
    ctx.stroke()
    ctx.restore()
  }
}

const drawScalesTitles: Plugin = {
  id: 'drawScalesTitles',
  afterDraw(chart, args, options) {
    const { ctx, chartArea } = chart
    const { left, top, width, height } = chartArea

    ctx.save()
    ctx.fillStyle = '#545E6C'
    ctx.font = 'regular 12px Inter'
    ctx.textAlign = 'center'
    ctx.fillText(String(options.xTitle), left + width / 2, height + 6)
    ctx.fillText(String(options.yTitle), left + 10, top + height / 2)
    ctx.fillText('Parto', left + 2, height + top + 40)
    ctx.fillText('1 Ano', left + width / 2, height + top + 40)
    ctx.fillText('2 Anos', width + left, height + top + 40)
    ctx.restore()
  }
}

const defaultLineProperties = {
  type: 'line' as const,

  fill: false,
  borderColor: '#545E6C',

  tension: 0.4,
  borderWidth: 1,
  pointRadius: 0,
  pointHoverRadius: 0,
  pointHitRadius: 0
}

export const getDataBy = (percentiles: BabyGrowthPercentiles, annotations: IAnnotation[]): ChartData<'line'> => ({
  labels: Array.from(percentiles[3], (_, i) => i),
  datasets: [
    {
      data: percentiles[3],
      ...defaultLineProperties
    },
    {
      data: percentiles[15],
      ...defaultLineProperties
    },
    {
      data: percentiles[50],
      ...defaultLineProperties
    },
    {
      data: percentiles[85],
      ...defaultLineProperties
    },
    {
      data: percentiles[97],
      ...defaultLineProperties
    },
    {
      type: 'scatter' as 'line',
      data: annotations.map(annotation => ({ x: annotation.x, y: annotation.y }))
    }
  ]
})

const getHigherYAxis = (annotations: IAnnotation[], percentiles: BabyGrowthPercentiles): number => {
  const higherPercentile = Math.max(...percentiles[97])
  const higherAnnotation = Math.max(...annotations.map(annotation => annotation.y))
  const higherValue = Math.max(higherPercentile, higherAnnotation)
  return Math.ceil(higherValue + 2)
}

const getLowerYAxis = (annotations: IAnnotation[], percentiles: BabyGrowthPercentiles): number => {
  const lowerPercentile = Math.min(...percentiles[3])
  const lowerAnnotation = Math.min(...annotations.map(annotation => annotation.y))
  const lowerValue = Math.min(lowerPercentile, lowerAnnotation)
  return Math.ceil(Math.max(0, lowerValue - 15))
}

export const getOptionsBy = (
  measures: IAxisLabels,
  percentiles: BabyGrowthPercentiles,
  annotations: IAnnotation[]
): ChartOptions<'line'> => ({
  maintainAspectRatio: false,

  layout: {
    padding: {
      bottom: 10
    }
  },

  elements: {
    point: {
      radius: 5,
      backgroundColor: '#1F6ECD',
      hitRadius: 8,
      hoverRadius: 8
    }
  },
  plugins: {
    drawScalesTitles: {
      xTitle: measures.x,
      yTitle: measures.y
    },
    legend: { display: false },
    title: { display: false },
    tooltip: {
      callbacks: {
        title: ctx => {
          const rawValues: any = ctx[0].raw
          return `${rawValues.x.toFixed(0)} meses`
        },
        label: ctx => {
          return `${ctx.formattedValue} ${measures.y}`
        }
      }
    }
  },
  scales: {
    y: {
      position: 'left',
      grid: {
        drawBorder: false,
        drawTicks: false,
        color: '#ECEFF4'
      },
      max: getHigherYAxis(annotations, percentiles),
      min: getLowerYAxis(annotations, percentiles),
      ticks: {
        stepSize: 2,
        color: '#545E6C',
        font: {
          size: 12
        }
      },
      beginAtZero: true
    },
    y1: {
      position: 'right',
      grid: {
        drawOnChartArea: false,
        drawTicks: false,
        color: '#1F6ECD'
      },
      ticks: {
        color: '#1F6ECD',
        font: {
          size: 12
        }
      },
      max: getHigherYAxis(annotations, percentiles),
      min: getLowerYAxis(annotations, percentiles),
      afterTickToLabelConversion: function (chart: any) {
        const percentilesIndexes = Object.keys(percentiles)
        const percentileTicks = [...Object.values(percentiles).map(percentile => percentile[percentile.length - 1])]
        chart.ticks = []
        percentileTicks.forEach((tick, index) => {
          chart.ticks.push({ value: tick, label: percentilesIndexes[index] })
        })
      }
    },
    y2: {
      position: 'right',
      grid: {
        display: false,
        // drawBorder: false,
        drawTicks: false
      },
      ticks: {
        stepSize: 2,
        color: '#545E6C',
        font: {
          size: 12
        }
      },
      afterBuildTicks: axis => {
        axis.ticks = [...axis.chart.scales.y.ticks]
        axis.min = axis.chart.scales.y.min
        axis.max = axis.chart.scales.y.max
      }
    },
    x: {
      type: 'linear',
      position: 'bottom',
      grid: {
        drawBorder: false,
        drawTicks: false,
        color: '#ECEFF4'
      },
      afterTickToLabelConversion: function (chart: any) {
        const ticks = chart.ticks
        chart.ticks = []

        ticks.forEach(tick => {
          if (tick.value % 2 === 0) {
            chart.ticks.push({ value: tick.value, label: tick.label })
          }
        })
      },
      beginAtZero: true,
      max: 24,
      suggestedMin: 0,
      ticks: {
        stepSize: 2,
        color: '#545E6C',
        padding: 10,
        font: {
          size: 12
        }
      }
    }
  }
})

export const ChartPlugins = { drawBorderRadius, drawScalesTitles }
